import { Product } from "@transfr-inc/dashboard-components/constants";

import { parseJwt } from "@lib/browser-utils";
import { getRoleFeatures } from "@models/role";
import { useContainer } from "@src/contexts/container-context";
import { Role } from "@src/models/role";
import { useCallback, useMemo } from "react";
import { useCurrentUser, useUserClassroomCourses } from "./account";
import { useCourseBuilderConfigEnabled } from "./configuration";
import { useClassrooms, useOrganizationDetails } from "./organization";

/**
 * Hook to check if user is logged in
 */
export function useIsLoggedIn() {
  const currentUser = useCurrentUser();
  return currentUser != null;
}

/**
 * Hook to get user role
 */
export function useRole() {
  const currentUser = useCurrentUser();
  return useMemo(() => {
    if (!currentUser) {
      return;
    }

    const token = parseJwt(currentUser?.token);
    return token?.act;
  }, [currentUser]);
}

/**
 * Whether this session is a client-services user acting as another user.
 *
 * Read from the persisted current user, which is the same signal the
 * impersonation banner renders from and the only one that survives a page
 * reload — the account UI store's `impersonating` flag is in-memory state for
 * the in-flight impersonation login and reads false after a refresh.
 */
export function useIsImpersonating() {
  const currentUser = useCurrentUser();
  return Boolean(currentUser?.impersonation);
}

/**
 * Hook to check whether an organization is integration-managed, from the
 * `isIntegrationOrg` flag on the org-details response. This is the
 * authoritative, role-independent signal, unlike the per-user
 * `integrations`/`idp` fields on the login response, which vary by role and
 * login method.
 *
 * `isResolved` is false until the org details have loaded, so callers can hold
 * off on showing integration-restricted actions until the answer is known
 * rather than briefly flashing them.
 */
export function useIsIntegrationOrg(orgCode) {
  const { data: organization, isSuccess: isResolved } =
    useOrganizationDetails(orgCode);
  const isIntegrationOrg = useMemo(
    () => Boolean(organization?.isIntegrationOrg),
    [organization]
  );
  return { isIntegrationOrg, isResolved };
}

/**
 * Returns a predicate that reports whether a given org code is
 * integration-managed, covering the org itself AND each of its children.
 */
export function useIntegrationOrgCodes(orgCode) {
  const { data: organization, isSuccess: isResolved } =
    useOrganizationDetails(orgCode);
  const integrationOrgCodes = useMemo(() => {
    const codes = new Set();
    if (!organization) {
      return codes;
    }

    if (organization.isIntegrationOrg) {
      codes.add(organization.code);
    }
    for (const child of organization.organizations ?? []) {
      if (child.isIntegrationOrg) {
        codes.add(child.code);
      }
    }

    return codes;
  }, [organization]);

  const isIntegrationOrgCode = useCallback(
    (code) => integrationOrgCodes.has(code),
    [integrationOrgCodes]
  );

  return { isIntegrationOrgCode, integrationOrgCodes, isResolved };
}

/**
 * Hook to check if integration preconfig is enabled
 */
export function useIntegrationPreconfigEnabled() {
  const { toggles } = useContainer();
  return toggles.isIntegrationPreconfigEnabled;
}

/**
 * Hook to check if learner dashboard is enabled
 */
export function useLearnerDashboardEnabled() {
  const { toggles } = useContainer();
  return toggles.learnerDashboardEnabled;
}

/**
 * Hook to check if mission control is enabled
 */
export function useMissionControlEnabled() {
  const { toggles } = useContainer();
  const currentUser = useCurrentUser();
  return useMemo(
    () => toggles.missionControlEnabled && currentUser?.missionControl,
    [toggles.missionControlEnabled, currentUser]
  );
}

/**
 * Hook to check if student prereq toggle is allowed
 */
export function useAllowStudentPrereqToggle() {
  const { toggles } = useContainer();
  return toggles.allowStudentPrereqToggle;
}

/**
 * Hook to get user products
 */
export function useProducts() {
  const currentUser = useCurrentUser();
  return useMemo(() => currentUser?.products, [currentUser]);
}

/**
 * The set of `Product` ids the outcomes dashboard offers metrics for, and whether
 * that set has finished resolving.
 *
 * Derived from the lines the viewer's active classrooms teach, not the org's
 * licenses: a licensed line with no classroom has no outcomes to report, and a
 * withdrawn licence keeps the data its classrooms produced. `useClassrooms`
 * scopes itself to the viewer, so the set spans an instructor's own classrooms,
 * an admin's org, or every child org for a multi-org viewer.
 *
 * Callers gate on `isResolved` rather than filter against a set that is still
 * widening, which would briefly hide metrics the viewer has.
 *
 * @returns {{ products: Set<number|string>, isResolved: boolean }}
 */
export function useOutcomesLicensedProducts() {
  const role = useRole();
  const { organizationCode, userId } = useCurrentUser() ?? {};

  const {
    data: classrooms,
    isSuccess: classroomsSuccess,
    isPlaceholderData: classroomsIsPlaceholder,
  } = useClassrooms({ userId, role, orgCode: organizationCode });

  const products = useMemo(() => {
    const set = new Set();
    for (const classroom of Array.isArray(classrooms) ? classrooms : []) {
      for (const productId of classroom.products ?? []) {
        set.add(productId);
      }
      if (classroom.isProfessionalLearning) {
        set.add(Product.BASICS);
      }
    }

    return set;
  }, [classrooms]);

  // `useClassrooms` seeds `placeholderData: []`, which reports success while the
  // real stats are in flight, so the set would read as resolved off an empty one.
  const isResolved = organizationCode
    ? classroomsSuccess && !classroomsIsPlaceholder
    : true;

  return { products, isResolved };
}

/**
 * Hook to get user classroom products
 */
export function useUserClassroomProducts() {
  const currentUser = useCurrentUser();
  const userId = currentUser?.userId;
  const { data: userClassroomCourses } = useUserClassroomCourses(userId);

  return useMemo(
    () =>
      userClassroomCourses
        ? new Set(userClassroomCourses.flatMap((r) => r.products))
        : new Set(),
    [userClassroomCourses]
  );
}

/**
 * Hook to get user features based on role
 */
export function useFeatures() {
  const role = useRole();
  return useMemo(() => {
    if (!role) {
      return {};
    }

    return getRoleFeatures(role) ?? {};
  }, [role]);
}

/**
 * Hook to check if preview mode is enabled.
 * Checks for 'preview' feature and validates user role.
 */
export function usePreviewClassroomModeEnabled() {
  const role = useRole();

  const {
    toggles: { isPreviewClassroomEnabled },
  } = useContainer();

  return useMemo(() => {
    const allowedRoles = [Role.INSTRUCTOR.id, Role.ORG_ADMIN.id];

    return isPreviewClassroomEnabled && allowedRoles.includes(role);
  }, [role]);
}

/**
 * Hook to check if multi product classroom is enabled
 */
export function useMultiProductClassroomEnabled() {
  const { toggles } = useContainer();
  return toggles.isMultiProductClassroomEnabled;
}

export function useArchiveClassroomEnabled(orgCode) {
  const { canArchiveClassroomEnabled } = useFeatures();
  const { isIntegrationOrg, isResolved } = useIsIntegrationOrg(orgCode);
  const { toggles } = useContainer();

  return (
    toggles.isArchiveClassroomEnabled &&
    canArchiveClassroomEnabled &&
    isResolved &&
    !isIntegrationOrg
  );
}

export function useArchiveUserEnabled(orgCode) {
  const { canArchiveUserEnabled } = useFeatures();
  const { isIntegrationOrg, isResolved } = useIsIntegrationOrg(orgCode);
  const { toggles } = useContainer();

  return (
    toggles.isArchiveUserEnabled &&
    canArchiveUserEnabled &&
    isResolved &&
    !isIntegrationOrg
  );
}

export function useArchiveClassroomVisible() {
  const { canArchiveClassroomEnabled } = useFeatures() || {};
  const { toggles } = useContainer();

  return toggles.isArchiveClassroomEnabled && canArchiveClassroomEnabled;
}

export function useArchiveUserVisible() {
  const { canArchiveUserEnabled } = useFeatures() || {};
  const { toggles } = useContainer();

  return toggles.isArchiveUserEnabled && canArchiveUserEnabled;
}

export function useDeleteUserAllowed(orgCode) {
  const { isIntegrationOrg, isResolved } = useIsIntegrationOrg(orgCode);
  return isResolved && !isIntegrationOrg;
}

export function useDeleteClassroomAllowed(orgCode) {
  const { isIntegrationOrg, isResolved } = useIsIntegrationOrg(orgCode);
  return isResolved && !isIntegrationOrg;
}

export function useCourseBuilderEnabled() {
  const { data: courseBuilderConfigEnabled } = useCourseBuilderConfigEnabled();
  return !!courseBuilderConfigEnabled;
}
